import { useCallback, useRef, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  getTeamVerification,
  IqQuestion,
  postTeamVerificationAnswers,
  TeamVerificationStatus,
} from '../api';
import { useAuthToken } from './useAuthToken';
import { useAsyncEffect } from './useAsyncEffect';
import { useToaster } from './useToaster';

/**
 * Typed view of the LaunchDarkly flags this hook reads. Typing `useFlags`
 * prevents a silent typo (e.g. `releaseIdolgy`) from reading `undefined` and
 * disabling the feature with no compile error.
 */
interface IdentityVerificationFlags {
  releaseIdology?: boolean;
}

export interface UseIdentityVerificationResult {
  /** Whether the `release-idology` flag is on. When off, the prompt is hidden. */
  isEnabled: boolean;
  /** Step-up verification state from the underwriting service. */
  status: TeamVerificationStatus;
  /** IQ questions forwarded to the modal flow (SPX-2113). */
  items: IqQuestion[];
  /** True while the initial verification fetch is in flight. */
  isLoading: boolean;
  /** True once the user attempts to submit without completing verification. */
  hasError: boolean;
  /**
   * Returns `true` when the application may progress (verification complete or
   * not required / flag off). Returns `false` and reveals the inline "Required"
   * error when step-up is required but not yet complete.
   */
  guardVerification: () => boolean;
  /** Whether the IQ questions modal is open. */
  isModalOpen: boolean;
  /** Open the IQ questions modal. */
  openModal: () => void;
  /**
   * Submit IQ answers, then refetch verification status. On success the modal
   * closes and status updates to `complete`. On error a toast is shown and the
   * error is re-thrown so the modal stays open for retry.
   */
  submitAnswers: (answers: Record<string, string>) => Promise<void>;
}

/**
 * Drives the "Verify Identity" prompt on the review page (SPX-2112).
 *
 * Reads the `release-idology` LaunchDarkly flag, fetches the merchant's step-up
 * verification state from the underwriting service, and exposes a
 * `guardVerification` gate mirroring `useRequiredDocumentGate` so the review
 * page can block submission until identity is verified.
 */
export function useIdentityVerification(): UseIdentityVerificationResult {
  const flags = useFlags<IdentityVerificationFlags>();
  const isEnabled = Boolean(flags.releaseIdology);
  const { authToken } = useAuthToken();
  const { toaster, toast } = useToaster();

  const [status, setStatus] = useState<TeamVerificationStatus>(null);
  const [items, setItems] = useState<IqQuestion[]>([]);
  const [isLoading, setIsLoading] = useState(isEnabled);
  const [hasError, setHasError] = useState(false);

  // Hold the latest status in a ref so `guardVerification` always reads the
  // current value when Submit is clicked, even if the click fires before the
  // render that captured the newest status (mirrors useRequiredDocumentGate).
  const statusRef = useRef<TeamVerificationStatus>(null);

  // Mirror `isLoading` into a ref so `guardVerification` can tell "status not
  // loaded yet" apart from "step-up not required" at click time — both leave
  // `statusRef.current` null, so without this a Submit during the in-flight
  // fetch would slip through the gate.
  const isLoadingRef = useRef(isEnabled);

  useAsyncEffect(
    async isMounted => {
      // Skip the network call entirely when the feature is off.
      if (!isEnabled || !authToken) {
        isLoadingRef.current = false;
        setIsLoading(false);
        return;
      }
      isLoadingRef.current = true;
      setIsLoading(true);
      try {
        const data = await getTeamVerification(authToken);
        if (!isMounted()) return;
        statusRef.current = data.questions.status;
        setStatus(data.questions.status);
        setItems(data.questions.items);
      } catch (error) {
        if (!isMounted()) return;
        // Fail open on the data fetch: a verification lookup error should not
        // hard-block the prompt from rendering its default state. The submit
        // gate still requires a `complete` status before progression.
        console.error('[identity-verification] failed to load status', error);
      } finally {
        isLoadingRef.current = false;
        if (isMounted()) setIsLoading(false);
      }
    },
    [isEnabled, authToken],
  );

  const guardVerification = useCallback((): boolean => {
    // Not gating when the feature is off.
    if (!isEnabled) return true;
    // Status not loaded yet — don't let a submit slip through while the fetch
    // is in flight (the submit button is also disabled while loading).
    if (isLoadingRef.current) return false;
    // Step-up isn't required, or it's already complete.
    if (statusRef.current === null || statusRef.current === 'complete') {
      return true;
    }
    setHasError(true);
    return false;
  }, [isEnabled]);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => setIsModalOpen(true);

  const submitAnswers = async (answers: Record<string, string>) => {
    if (!authToken) return;

    const answerArray = Object.entries(answers).map(([type, answer]) => ({
      type,
      answer,
    }));

    try {
      await postTeamVerificationAnswers(authToken, answerArray);
      const data = await getTeamVerification(authToken);
      statusRef.current = data.questions.status;
      setStatus(data.questions.status);
      setItems(data.questions.items);
      setIsModalOpen(false);
    } catch (error) {
      toaster(
        toast.error(
          error,
          'There was a problem submitting your answers. Please try again.',
        ),
      );
      throw error;
    }
  };

  return {
    isEnabled,
    status,
    items,
    isLoading,
    hasError,
    guardVerification,
    isModalOpen,
    openModal,
    submitAnswers,
  };
}
